'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { gatewayClient } from '../api';
import type { UserSession, LoginResponse, UserProfile, LoginRequest, RegisterRequest } from '../types/auth';

interface AuthContextType {
  user: UserSession | null;
  profile: UserProfile | null;
  // EUR cents. Null = not loaded yet (or logged out). Sourced from
  // GET /user-balance, not from the user object - the backend stopped
  // inlining balance on UserResponseDto.
  balanceCents: number | null;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<LoginResponse>;
  verifyTwoFactor: (pending2faToken: string, code: string) => Promise<UserSession>;
  register: (data: RegisterRequest) => Promise<boolean>;
  confirmEmail: (token: string) => Promise<UserSession>;
  googleLogin: (code: string, referral?: string) => Promise<LoginResponse>;
  facebookLogin: (code: string, referral?: string) => Promise<LoginResponse>;
  githubLogin: (code: string, referral?: string) => Promise<LoginResponse>;
  twitterLogin: (code: string, codeVerifier: string, referral?: string) => Promise<LoginResponse>;
  refreshProfile: () => Promise<void>;
  refreshBalance: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_KEY = 'aceb_user';

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [user, setUser] = useState<UserSession | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hydratedRef = useRef(false);

  // Pulls the ledger-head balance for the signed-in user. Best-effort: a
  // transient failure leaves the previously-known value in place so the
  // Cart / Account views don't flicker to €0.00.
  const fetchBalance = useCallback(async () => {
    try {
      const summary = await gatewayClient.balance.getMyBalance();
      setBalanceCents(summary.balanceCents);
    } catch { /* keep previous value */ }
  }, []);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const hydrate = async () => {
      try {
        const userData = await gatewayClient.auth.getUser();
        if (userData?._id) {
          setUser({
            id: userData._id,
            email: userData.email,
            username: userData.username,
            token: '',
          });
          setProfile(userData);
          localStorage.setItem(USER_KEY, JSON.stringify(userData));
          fetchBalance();
        }
      } catch {
        localStorage.removeItem(USER_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    // The auth cookie is HttpOnly, so the stored session is our only signal
    // that a login ever happened here. Without it, skip the GET /user
    // roundtrip entirely - it would just 403.
    const stored = localStorage.getItem(USER_KEY);
    if (!stored) {
      setIsLoading(false);
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      setUser({
        id: parsed._id ?? parsed.id,
        email: parsed.email,
        username: parsed.username,
        token: parsed.token ?? '',
      });
      setProfile(parsed);
    } catch {
      localStorage.removeItem(USER_KEY);
      setIsLoading(false);
      return;
    }

    hydrate();
  }, []);

  const persistSession = useCallback((session: UserSession) => {
    setUser(session);
    localStorage.setItem(USER_KEY, JSON.stringify(session));
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setProfile(null);
    setBalanceCents(null);
    localStorage.removeItem(USER_KEY);
  }, []);

  const login = useCallback(async (data: LoginRequest): Promise<LoginResponse> => {
    clearSession();
    const result = await gatewayClient.auth.login(data);
    // 2FA challenge: do NOT persist - caller must finish via verifyTwoFactor().
    if (result.requires2FA) {
      return result;
    }
    persistSession(result);
    try {
      const userData = await gatewayClient.auth.getUser();
      setProfile(userData);
      fetchBalance();
    } catch { /* profile fetch is best-effort */ }
    return result;
  }, [clearSession, persistSession, fetchBalance]);

  const verifyTwoFactor = useCallback(
    async (pending2faToken: string, code: string): Promise<UserSession> => {
      const session = await gatewayClient.auth.twoFactorVerify({ pending2faToken, code });
      persistSession(session);
      try {
        const userData = await gatewayClient.auth.getUser();
        setProfile(userData);
        fetchBalance();
      } catch { /* profile fetch is best-effort */ }
      return session;
    },
    [persistSession, fetchBalance],
  );

  const register = useCallback(async (data: RegisterRequest): Promise<boolean> => {
    return await gatewayClient.auth.register(data);
  }, []);

  const confirmEmail = useCallback(async (token: string): Promise<UserSession> => {
    clearSession();
    const session = await gatewayClient.auth.confirmEmail(token);
    persistSession(session);
    try {
      const userData = await gatewayClient.auth.getUser();
      setProfile(userData);
      fetchBalance();
    } catch { /* profile fetch is best-effort */ }
    return session;
  }, [clearSession, persistSession, fetchBalance]);

  const googleLogin = useCallback(async (code: string, referral?: string): Promise<LoginResponse> => {
    clearSession();
    const session = await gatewayClient.auth.googleAuth(code, referral);
    // 2FA challenge: do NOT persist - caller must finish via verifyTwoFactor().
    if (session.requires2FA) {
      return session;
    }
    persistSession(session);
    try {
      const userData = await gatewayClient.auth.getUser();
      setProfile(userData);
      fetchBalance();
    } catch { /* profile fetch is best-effort */ }
    return session;
  }, [clearSession, persistSession, fetchBalance]);

  const facebookLogin = useCallback(async (code: string, referral?: string): Promise<LoginResponse> => {
    clearSession();
    const session = await gatewayClient.auth.facebookAuth(code, referral);
    // 2FA challenge: do NOT persist - caller must finish via verifyTwoFactor().
    if (session.requires2FA) {
      return session;
    }
    persistSession(session);
    try {
      const userData = await gatewayClient.auth.getUser();
      setProfile(userData);
      fetchBalance();
    } catch { /* profile fetch is best-effort */ }
    return session;
  }, [clearSession, persistSession, fetchBalance]);

  const githubLogin = useCallback(async (code: string, referral?: string): Promise<LoginResponse> => {
    clearSession();
    const session = await gatewayClient.auth.githubAuth(code, referral);
    // 2FA challenge: do NOT persist - caller must finish via verifyTwoFactor().
    if (session.requires2FA) {
      return session;
    }
    persistSession(session);
    try {
      const userData = await gatewayClient.auth.getUser();
      setProfile(userData);
      fetchBalance();
    } catch { /* profile fetch is best-effort */ }
    return session;
  }, [clearSession, persistSession, fetchBalance]);

  const twitterLogin = useCallback(async (code: string, codeVerifier: string, referral?: string): Promise<LoginResponse> => {
    clearSession();
    const session = await gatewayClient.auth.twitterAuth(code, codeVerifier, referral);
    // 2FA challenge: do NOT persist - caller must finish via verifyTwoFactor().
    if (session.requires2FA) {
      return session;
    }
    persistSession(session);
    try {
      const userData = await gatewayClient.auth.getUser();
      setProfile(userData);
      fetchBalance();
    } catch { /* profile fetch is best-effort */ }
    return session;
  }, [clearSession, persistSession, fetchBalance]);

  const refreshProfile = useCallback(async () => {
    try {
      const userData = await gatewayClient.auth.getUser();
      if (userData?._id) {
        setProfile(userData);
        setUser({
          id: userData._id,
          email: userData.email,
          username: userData.username,
          token: '',
        });
      }
    } catch { /* silently fail */ }
  }, []);

  const refreshBalance = useCallback(async () => {
    await fetchBalance();
  }, [fetchBalance]);

  const logout = useCallback(async () => {
    clearSession();
    try {
      await gatewayClient.auth.logout();
    } catch { /* cookie cleared server-side best-effort */ }
  }, [clearSession]);

  // Self-evict when the backend tells us the account was disabled or deleted
  // mid-session. apiClient emits this on any 403 carrying errorCode
  // USER_INACTIVE. We clear local state, best-effort kill the server cookie,
  // and bounce to home - same shape as a manual logout.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onRevoked = () => {
      clearSession();
      gatewayClient.auth.logout().catch(() => {});
      // Hard navigation so any in-flight Next.js client cache is discarded.
      window.location.assign('/');
    };
    window.addEventListener('aceb:auth-revoked', onRevoked);
    return () => window.removeEventListener('aceb:auth-revoked', onRevoked);
  }, [clearSession]);

  const value = React.useMemo(
    () => ({ user, profile, balanceCents, isLoading, login, verifyTwoFactor, register, confirmEmail, googleLogin, facebookLogin, githubLogin, twitterLogin, refreshProfile, refreshBalance, logout }),
    [user, profile, balanceCents, isLoading, login, verifyTwoFactor, register, confirmEmail, googleLogin, facebookLogin, githubLogin, twitterLogin, refreshProfile, refreshBalance, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
